import classNames from 'classnames';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useMatch from '@/hooks/useMatch';
import useStorage from '@/hooks/useStorage';
import type { Biog, BiogWord } from '@/types/visualization';
import { EVENT } from '@/utils/event';
import { ROUTER_PATH } from '@/utils/router';

import Ball from './Ball';

enum Position {
  centerDiameter = 'centerDiameter',
  mediumDiameter = 'mediumDiameter',
  outerDiameter = 'outerDiameter'
}

export interface IBall {
  width: number;
  bgUrl: string;
  icon: string;
  position: Position;
  num: number;
  word?: string;
  id?: string;
}

interface IProps {
  handleClick?: (item: BiogWord) => void;
  keywords?: Biog[] | undefined;
  setOpen?: (open: boolean) => void;
  className?: string;
}

const centerBalls = [
  {
    width: 72,
    bgUrl: 'https://mindverseglobal.mindverse.com/front-img/crm/1743574251036/%E5%A4%A7%402x.png',
    icon: '🌈'
  },
  {
    width: 68,
    bgUrl: 'https://mindverseglobal.mindverse.com/front-img/crm/1743580559507/%E4%B8%AD-1%402x.png',
    icon: '🌟'
  },
  {
    width: 68,
    bgUrl: 'https://mindverseglobal.mindverse.com/front-img/crm/1743580737409/%E4%B8%AD-2%402x.png',
    icon: '🏃'
  }
];

const mediumBalls = [
  {
    width: 54,
    bgUrl: 'https://mindverseglobal.mindverse.com/front-img/crm/1743582446395/%E5%B0%8F-2%402x.png',
    icon: '📖'
  },
  {
    width: 54,
    bgUrl: 'https://mindverseglobal.mindverse.com/front-img/crm/1743582446395/%E5%B0%8F-3%402x.png',
    icon: '📚'
  },
  {
    width: 54,
    bgUrl: 'https://mindverseglobal.mindverse.com/front-img/crm/1743582446395/%E5%B0%8F-4%402x.png',
    icon: '🍦'
  },
  {
    width: 54,
    bgUrl: 'https://mindverseglobal.mindverse.com/front-img/crm/1743582446395/%E5%B0%8F-5%402x.png',
    icon: '🎨'
  },
  {
    width: 54,
    bgUrl: 'https://mindverseglobal.mindverse.com/front-img/crm/1743582437579/%E5%B0%8F-1%402x.png',
    icon: '🫧'
  }
];

const defaultBalls: IBall[] = [
  ...centerBalls.map((item, index) => ({
    ...item,
    num: index + 1,
    position: Position.centerDiameter
  })),
  ...mediumBalls.map((item, index) => ({
    ...item,
    num: index + 1,
    position: Position.mediumDiameter
  }))
];

const Diameters = {
  [Position.centerDiameter]: 160,
  [Position.mediumDiameter]: 320,
  [Position.outerDiameter]: 456
};

const ballCountMap = {
  [Position.centerDiameter]: centerBalls.length,
  [Position.mediumDiameter]: mediumBalls.length,
  [Position.outerDiameter]: 0
};

const ballSpeedMap = {
  [Position.centerDiameter]: 100,
  [Position.mediumDiameter]: 600,
  [Position.outerDiameter]: 800
};

const BiogSpace = (props: IProps) => {
  const { handleClick, keywords: _keywords, setOpen, className } = props;
  const keywords = useMemo(() => {
    return _keywords?.sort((a, b) => (b.weight || 0) - (a.weight || 0)).slice(0, 8) || [];
  }, [_keywords]);

  const searchParams = useSearchParams();
  const pathName = usePathname();
  const paramBiog = useMemo(() => {
    return searchParams.get('chosenBiog');
  }, [searchParams]);

  const _inDetail = !!useMatch(ROUTER_PATH.personalAIMemoryDetail);
  const _inMobileDetail = !!useMatch(ROUTER_PATH.personalAIMemoryDetailMobile);
  const inDetail = _inDetail || _inMobileDetail;

  const [_, setLayoutSecondaryPanel] = useStorage('layoutSecondaryPanel');
  const [layoutSize] = useStorage('layoutSize');
  const [inMobile, setInMobile] = useState(layoutSize === LAYOUT_SIZE.SMALL);

  useEffect(() => {
    setInMobile(layoutSize === LAYOUT_SIZE.SMALL);
  }, [layoutSize]);

  useEffect(() => {
    if (
      paramBiog &&
      (pathName === ROUTER_PATH.personalAI || pathName === ROUTER_PATH.personalAIMobile)
    ) {
      setOpen?.(true);
    }
  }, [paramBiog, pathName]);

  useEffect(() => {
    if (inDetail) {
      if (inMobile) {
        dispatchEvent(new CustomEvent(EVENT.HIDE_MOBILE_BIOG));
      } else {
        setLayoutSecondaryPanel('');
      }

      setOpen?.(false);
    }
  }, [inDetail, inMobile]);

  const renderBalls = () => {
    const balls = defaultBalls.map((item, index) => {
      return index < keywords.length
        ? {
            ...item,
            icon: keywords[index].icon || item.icon,
            word: keywords[index].word,
            id: keywords[index].id
          }
        : item;
    });

    return balls.map((item, index) => {
      return (
        <Ball
          key={index}
          ball={item}
          handleClick={() => handleClick?.(item)}
          max={ballCountMap[item.position]}
          reverse={item.position === Position.mediumDiameter}
          speed={ballSpeedMap[item.position]}
          translate={Diameters[item.position] / 2}
        />
      );
    });
  };

  return (
    <div className={classNames('relative', className)}>
      <div
        className="flex items-center justify-center bg-[rgba(255,255,255,0.1)] rounded-full blur-[2px]"
        style={{
          width: `${Diameters[Position.outerDiameter]}px`,
          height: `${Diameters[Position.outerDiameter]}px`
        }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[rgba(255,255,255,0.1)] rounded-full blur-[2px] "
        style={{
          width: `${Diameters[Position.mediumDiameter]}px`,
          height: `${Diameters[Position.mediumDiameter]}px`
        }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[rgba(255,255,255,0.1)] rounded-full blur-[2px] "
        style={{
          width: `${Diameters[Position.centerDiameter]}px`,
          height: `${Diameters[Position.centerDiameter]}px`
        }}
      />
      {renderBalls()}
    </div>
  );
};

export default BiogSpace;
